import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import * as dayjs from 'dayjs';
import { chain } from 'lodash-es';

import { BusinessAccountTypeNameMapping, BusinessAccountWithChildrenDto, ChildUserAccountDto } from 'src/app/core/dtos/dto';
import { AccountService } from 'src/app/core/services/account.service';
import { ConfirmModalComponent } from 'src/app/account/components/confirm-modal/confirm-modal.component';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { RegStatusEnum, ScannedDocumentStatusEnum } from 'src/app/core/dtos/enum';

@Component({
  selector: 'app-search-child-account',
  templateUrl: './search-child-account.component.html',
  styleUrls: ['./search-child-account.component.scss']
})
export class SearchChildAccountComponent implements OnInit {

  @Input() businessAccountTypeNameMapping: BusinessAccountTypeNameMapping;
  @Input() businessAccount: BusinessAccountWithChildrenDto;
  @Output() aliasSignIn = new EventEmitter<number>();

  @ViewChild("signInAsChildModal") signInAsChildModal: ModalComponent;
  @ViewChild("disownModal") disownModal: ConfirmModalComponent;

  allChildAccounts: ChildUserAccountDto[];
  filteredChildAccounts: ChildUserAccountDto[];
  selectedChildUser: ChildUserAccountDto;
  searchForm: FormGroup;

  constructor(
    private accountService: AccountService,
    private toastService: HotToastService,
  ) { }

  ngOnInit(): void {
    this.allChildAccounts = this.businessAccount.childUserAccounts;
    this.accountService.getChildAccountRegs()
      .subscribe(regsData => {
        for (const childAccount of this.allChildAccounts) {
          childAccount.registrations = regsData.content
            .filter(x => x.userAccountId === childAccount.userAccountId && x.regStatusEnum === RegStatusEnum.ScorePublished);
          childAccount.hasRejectedDocument = regsData.content
            .filter(x => (x.userAccountId === childAccount.userAccountId) && (x.regStatusEnum === RegStatusEnum.RegistrationActive) && (x.uploadedPhotoStatus === ScannedDocumentStatusEnum.Rejected || x.scannedDocumentStatus === ScannedDocumentStatusEnum.Rejected)).length > 0 && true;
        }
      });
    this.filteredChildAccounts = this.allChildAccounts;

    this.searchForm = new FormGroup({
      nameSearch: new FormControl(),
      regSearch: new FormControl(),
      dateSearch: new FormControl(),
    });
  }

  onSubmit(): void {
    let filteredList = [];

    const regSearchValue = this.searchForm.controls.regSearch.value;
    const dateSearchValue = this.searchForm.controls.dateSearch.value;
    const nameSearchValue = this.searchForm.controls.nameSearch.value;

    const regSearchEmpty = regSearchValue === null || regSearchValue === "";
    const dateSearchEmpty = dateSearchValue === null || dateSearchValue === "";
    const nameSearchEmpty = nameSearchValue === null || nameSearchValue === "";

    if (!regSearchEmpty || !dateSearchEmpty) {
      this.accountService.getChildAccountRegs().subscribe(regData => {
        filteredList = regData.content.filter(data => {
          const dateMatch = dateSearchEmpty || dayjs(data.testDateTime).format('YYYY-MM-DD') === dateSearchValue;
          const regMatch = regSearchEmpty || String(data.regId) === regSearchValue;
          return dateMatch && regMatch;
        });
        filteredList = filteredList.map(data => data.testTakerFullName);
        this.filteredChildAccounts = this.allChildAccounts.filter(account => {
          const nameMatch = nameSearchEmpty || account.fullName.toLowerCase().includes(nameSearchValue.toLowerCase());
          return filteredList.includes(account.fullName) && nameMatch;
        });
      });
    }
    else if (!nameSearchEmpty) {
      this.filteredChildAccounts = this.allChildAccounts.filter(account => account.fullName.toLowerCase().includes(nameSearchValue.toLowerCase()));
    } else {
      this.filteredChildAccounts = this.allChildAccounts;
    }
  }

  onDisownClick(childUserAccount): void {
    this.selectedChildUser = childUserAccount;
    this.disownModal.open();
  }

  disownChild(childToDisown): void {
    this.accountService.disownChildAccount(childToDisown.userAccountId)
      .subscribe(
        () => {
          this.filteredChildAccounts = this.filteredChildAccounts.filter(childAccount => childAccount.userAccountId !== childToDisown.userAccountId);
          this.toastService.show(childToDisown.fullName + " unlinked successfully.");
        },
        (error) => {
          let errorMsg = "Action failed.";
          if (error && error.reasons) {
            const reasons = chain(error.reasons).map(x => x.errorMessage).value();
            errorMsg = reasons.join("<br>");
          }
          this.toastService.show(errorMsg);
        });
  }

  onAliasClick(childUserAccount: ChildUserAccountDto): void {
    this.selectedChildUser = childUserAccount;
    this.signInAsChildModal.open();
  }

  signInAsChild(childUserAccount: ChildUserAccountDto): void {
    this.aliasSignIn.emit(childUserAccount.userAccountId);
  }

  getSelectedChildUserName(): string {
    return this.selectedChildUser?.fullName ?? "";
  }

}
